import { ComponentProps, createContext, ReactNode, useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { withStyles } from 'vopay';
import { Title as VTitle, CircleButton } from '..';
import styles from './Dialog.module.scss';
import { Dialog as RDialog, VisuallyHidden } from 'radix-ui';

const cn = withStyles(styles);

const DialogOpenContext = createContext(false);

type DialogRootProps = {
  open: boolean;
  children: ReactNode;
  onClose?: () => void;
};

export default function Dialog({ open, children, onClose }: DialogRootProps) {
  return (
    <DialogOpenContext.Provider value={open}>
      <RDialog.Root
        open={open}
        onOpenChange={(open) => {
          if (!open) onClose?.();
        }}
      >
        {children}
      </RDialog.Root>
    </DialogOpenContext.Provider>
  );
}

Dialog.Portal = RDialog.Portal;

type DialogContentProps = {
  children: ReactNode;
  closeButton?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
};

function Content({
  children,
  closeButton = false,
  closeOnOutsideClick = false,
  closeOnEscape = false,
  className,
}: DialogContentProps) {
  const open = useContext(DialogOpenContext);

  return (
    <AnimatePresence>
      {open && (
        <RDialog.Portal forceMount container={document.getElementById('modal-root')!}>
          <RDialog.Overlay forceMount asChild key="overlay">
            <motion.div
              className={cn('overlay')}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          </RDialog.Overlay>
          (
          <RDialog.Content
            key="content"
            forceMount
            onInteractOutside={(e) => {
              if (!closeOnOutsideClick) {
                e.preventDefault();
              }
            }}
            onEscapeKeyDown={(e) => {
              if (!closeOnEscape) {
                e.preventDefault();
              }
            }}
            asChild
          >
            <motion.div
              className={cn('content', className)}
              initial={{ top: '150%', opacity: 0 }}
              animate={{ top: '50%', opacity: 1, transition: { duration: 0.8, ease: [0, 1, 0, 1] } }}
              exit={{ top: '150%', opacity: 0, transition: { duration: 0.3, ease: [1, 0, 0.9, 0.98] } }}
            >
              {closeButton && (
                <RDialog.Close asChild>
                  <CircleButton className={cn('closeButton')} icon="close" size="large" circleFill />
                </RDialog.Close>
              )}

              {children}
            </motion.div>
          </RDialog.Content>
          )
        </RDialog.Portal>
      )}
    </AnimatePresence>
  );
}

Dialog.Content = Content;

Dialog.Trigger = RDialog.Trigger;

type TitleProps = ComponentProps<typeof VTitle> & {
  hidden?: boolean;
};

function Title({ hidden, ...props }: TitleProps) {
  if (hidden) {
    return (
      <VisuallyHidden.Root>
        <RDialog.Title asChild>
          <VTitle {...props} />
        </RDialog.Title>
      </VisuallyHidden.Root>
    );
  }

  return (
    <RDialog.Title asChild>
      <VTitle level="2" {...props} />
    </RDialog.Title>
  );
}

Dialog.Title = Title;
